import EventDuck from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck'
import { EVENT_ACTIONS } from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actions'
import type { DuckActionPayloadTuple } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import type {
  ManualOrderUpdatePayload,
  SectionApplyMetricsPayload
} from '@aytm/app/insights/dashboard/helpers/attributes'
import type { ItemReplacePayload } from '@aytm/app/insights/dashboard/helpers/items'
import type { ItemType, TargetType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type { MilestoneListModel } from '@aytm/app/insights/dashboard/models/types.ClientModel'

export const makeReassignSigTestLettersAction = () =>
  [EventDuck.duckName, EVENT_ACTIONS.REASSIGN_SIGTEST_LETTERS, {}] as DuckActionPayloadTuple<{}>

export const makeSectionAddEventAction = (sectionName: string, target: TargetType, referenceSectionName?: string) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.SECTION_ADD,
    {
      section: sectionName,
      target,
      referenceSection: referenceSectionName
    }
  ] as DuckActionPayloadTuple<{ section: string; target: TargetType; referenceSection?: string }>

export const makeSectionRenameEventAction = (sectionName: string, newSectionName: string) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.SECTION_RENAME,
    { section: sectionName, name: newSectionName }
  ] as DuckActionPayloadTuple<{ section: string; name: string }>

export const makeSectionDeleteEventAction = (sectionName: string, target: TargetType) =>
  [EventDuck.duckName, EVENT_ACTIONS.SECTION_DELETE, { section: sectionName, target }] as DuckActionPayloadTuple<{
    section: string
    target: TargetType
  }>

export const makeAttributeAddEventAction = (sectionName: string, attribute: string, target: TargetType) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.ATTRIBUTE_ADD,
    {
      section: sectionName,
      attribute,
      target
    }
  ] as DuckActionPayloadTuple<{ section: string; attribute: string; target: TargetType }>

export const makeGroupAddEventAction = (sectionName: string, groupName: string, target: TargetType) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.GROUP_ADD,
    {
      section: sectionName,
      group: groupName,
      target
    }
  ] as DuckActionPayloadTuple<{ section: string; group: string; target: TargetType }>

export const makeSectionReplaceEventAction = (stationarySectionName: string, droppedSectionName: string) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.SECTION_REPLACE,
    {
      section: stationarySectionName,
      dropped: droppedSectionName
    }
  ] as DuckActionPayloadTuple<{ section: string; dropped: string }>

export const makeApplyMetricsToSectionAction = (sectionName: string, target: TargetType, metricsId: string) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.SECTION_APPLY_METRICS,
    {
      section: sectionName,
      target,
      method: metricsId
    }
  ] as DuckActionPayloadTuple<SectionApplyMetricsPayload>

export const makePositionalTimelineUpdateEventAction = (zoomFactor: number, scrollOffset: number) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.TIMELINE_UPDATE,
    {
      timelineZoom: zoomFactor,
      timelineOffset: scrollOffset
    }
  ] as DuckActionPayloadTuple<{ timelineZoom: number; timelineOffset: number }>

export const makeMilestonesTimelineUpdateEventAction = (milestones: MilestoneListModel) =>
  [EventDuck.duckName, EVENT_ACTIONS.TIMELINE_UPDATE, { milestones }] as DuckActionPayloadTuple<{
    milestones: MilestoneListModel
  }>

export const makeColumnByUidBlinkEventAction = (columnUid: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.COLUMN_BLINK, { uid: columnUid }] as DuckActionPayloadTuple<{ uid: string }>

export const makeColumnByIndexBlinkEventAction = (columnIndex: number) =>
  [EventDuck.duckName, EVENT_ACTIONS.COLUMN_BLINK, { index: columnIndex }] as DuckActionPayloadTuple<{ index: number }>

export const makeItemAddEventAction = (itemUid: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_ADD, itemUid] as DuckActionPayloadTuple<string>

export const makeItemSelectEventAction = (itemUid: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_SELECT, itemUid] as DuckActionPayloadTuple<string>

export const makeItemPinnedEventAction = (itemUid: string, pinned: boolean) =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_PIN, { uid: itemUid, pinned }] as DuckActionPayloadTuple<{
    uid: string
    pinned: boolean
  }>

export const makeItemDeleteEventAction = (itemUid: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_DELETE, itemUid] as DuckActionPayloadTuple<string>

export const makeItemReplaceEventAction = (from: string, to: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_REPLACE, { from, to }] as DuckActionPayloadTuple<ItemReplacePayload>

export const makeSortOrderManualEventAction = () =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_ORDER_MANUAL, { isManual: true }] as DuckActionPayloadTuple<
    Pick<ManualOrderUpdatePayload, 'isManual'>
  >
export const makeSortOrderAutomaticEventAction = () =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_ORDER_MANUAL, { isManual: false }] as DuckActionPayloadTuple<
    Pick<ManualOrderUpdatePayload, 'isManual'>
  >

export const makeSortOrderPositionsEventAction = (positions: number[] | null) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.ITEM_ORDER_MANUAL,
    { positions }
  ] as DuckActionPayloadTuple<ManualOrderUpdatePayload>

export const makeSortOrderOnlyEventAction = (items: ItemType[] | null) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_SORT_ONLY_POSITIONS, { items }] as DuckActionPayloadTuple<{
    items: ItemType[] | null
  }>

export const makeItemDragTrackStateEventAction = (dragState: 'start' | 'end', index: number, uid?: string | null) =>
  [EventDuck.duckName, EVENT_ACTIONS.ITEM_DRAG_TRACK_STATE, { dragState, index, uid }] as DuckActionPayloadTuple<{
    index: number
    dragState: 'start' | 'end'
    uid?: string | null
  }>

export const makeItemDragListenStateEventAction = (
  listenState: 'enter' | 'leave' | 'drop',
  index: number,
  uid?: string,
  secondaryIndex?: number,
  secondaryUid?: string
) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.ITEM_DRAG_LISTEN_STATE,
    { index, listenState, uid, secondaryIndex, secondaryUid }
  ] as DuckActionPayloadTuple<{
    index: number
    listenState: 'enter' | 'leave' | 'drop'
    uid?: string
    secondaryIndex?: number
    secondaryUid?: string
  }>

export type AttributeSortEventType = { attribute: string; method: string; target?: TargetType }

export const makeSortByAttributeEventAction = (attributeSortEvent: AttributeSortEventType) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_SORT, attributeSortEvent] as DuckActionPayloadTuple<{
    attribute: string
    method: string
  }>

export const makeSortByAttributeEtcEventAction = (section: string, attribute: string, method: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_SORT, { section, attribute, method }] as DuckActionPayloadTuple<{
    section: string
    attribute: string
    method: string
  }>

export const makeSelectAttributeEventAction = (section: string, attribute: string, method: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.LEGEND_CLICK, { section, attribute, method }] as DuckActionPayloadTuple<{
    section: string
    attribute: string
    method: string
  }>

export const makeMetricsChangeEventAction = (metricsChangeEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_METRICS_CHANGE, metricsChangeEvent] as DuckActionPayloadTuple<unknown>

export const makeAttributeDeleteEventAction = (attributeDeleteEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_DELETE, attributeDeleteEvent] as DuckActionPayloadTuple<unknown>

export const makeAttributeAddByEventEventAction = (attributeAddEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_ADD, attributeAddEvent] as DuckActionPayloadTuple<unknown>

export const makeAttributeReplaceEventAction = (attributeReplaceEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.ATTRIBUTE_REPLACE, attributeReplaceEvent] as DuckActionPayloadTuple<unknown>

export const makeChangeCurrentWaveEventAction = (wave: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.WAVE_CHANGE, { wave }] as DuckActionPayloadTuple<{ wave: string }>

export const makeChangePresetEventAction = (preset: string, modifier: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.PRESET_CHANGE, { preset, modifier }] as DuckActionPayloadTuple<{
    preset: string
    modifier: unknown
  }>

export const makeShowSectionChangeEventAction = (section: string, target: TargetType, show: boolean) =>
  [EventDuck.duckName, EVENT_ACTIONS.SHOW_SECTION_CHANGE, { section, target, show }] as DuckActionPayloadTuple<{
    section: string
    target: TargetType
    show: boolean
  }>

export const makePrimaryConfidenceLevelChangeEventAction = (confidenceLevel: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.CONFIDENCE_LEVEL_CHANGE, { confidenceLevel }] as DuckActionPayloadTuple<{
    confidenceLevel: string
  }>

export const makeSecondaryConfidenceLevelChangeEventAction = (secondaryConfidenceLevel: string) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.SECONDARY_CONFIDENCE_LEVEL_CHANGE,
    {
      secondaryConfidenceLevel
    }
  ] as DuckActionPayloadTuple<{
    secondaryConfidenceLevel: string
  }>

export const makeChartScaleChangeEventAction = (scale: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.CHART_SCALE_CHANGE, { scale }] as DuckActionPayloadTuple<{ scale: unknown }>

export const makeChartTypeChangeEventAction = (type: string) =>
  [EventDuck.duckName, EVENT_ACTIONS.CHART_TYPE_CHANGE, { type }] as DuckActionPayloadTuple<{
    type: string
  }>

export const makeColorsChangeEventAction = (colors: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.COLORS_CHANGE, { colors }] as DuckActionPayloadTuple<{ colors: unknown }>

export const makeTrackTargetChangeEventAction = (trackTargetChangeEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.TRACK_TARGET_CHANGE, trackTargetChangeEvent] as DuckActionPayloadTuple<unknown>

export const makeTrackAttributeChangeEventAction = (trackAttributesChangeEvent: unknown) =>
  [
    EventDuck.duckName,
    EVENT_ACTIONS.TRACK_ATTRIBUTES_CHANGE,
    trackAttributesChangeEvent
  ] as DuckActionPayloadTuple<unknown>

export const makeCurrentSplitChangeEventAction = (currentSplitChangeEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.CURRENT_SPLIT_CHANGE, currentSplitChangeEvent] as DuckActionPayloadTuple<unknown>

export const makeSwapChangeEventAction = (swapChangeEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.SWAP_CHANGE, swapChangeEvent] as DuckActionPayloadTuple<unknown>

export const makeElementToggleEventAction = (elementToggleEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.ELEMENT_TOGGLE, elementToggleEvent] as DuckActionPayloadTuple<unknown>

export const changeShowFromToEventAction = (showFromToEvent: unknown) =>
  [EventDuck.duckName, EVENT_ACTIONS.SHOW_FROM_TO_CHANGE, showFromToEvent] as DuckActionPayloadTuple<unknown>
